import { IQuickBook, SettingCardViewAction } from '@monetize/types/app';
import isUndefined from 'lodash/isUndefined';
import { FC, ReactNode, useState } from 'react';
import { useUpdateAccountingConnectionStatus } from '../../../../../api/accountingService';
import { MAlert, MText } from '../../../../../components/Monetize';
import { SettingCardEdit } from '../../../../../components/Settings/SettingCardEdit';
import { SettingCardView } from '../../../../../components/Settings/SettingCardView';
import { QuickBooksForm } from './QuickBooksForm';
import { QuickBooksRefundSync } from './QuickBooksRefundSync';

interface QuickBooksConnectionProps {
  connection: IQuickBook;
  description?: ReactNode;
}

export const QuickBooksConnection: FC<QuickBooksConnectionProps> = ({
  connection,
  description,
}) => {
  const [mode, setMode] = useState<'VIEW' | 'EDIT'>('VIEW');
  const [showWarning, setShowWarning] = useState(false);

  const { mutate: updateConnection, isPending: isProcessing } =
    useUpdateAccountingConnectionStatus({
      meta: { showErrorToast: true },
    });

  const isEnabled = connection.enabled;

  const handleAction = (action: SettingCardViewAction) => {
    if (isProcessing) {
      return;
    }
    switch (action) {
      case 'ENABLE':
        updateConnection({ type: 'quickbooks', action: 'enable' });
        break;
      case 'DISABLE':
        updateConnection({ type: 'quickbooks', action: 'disable' });
        break;
      default:
        break;
    }
  };

  return (
    <>
      {mode === 'VIEW' && (
        <SettingCardView
          status={isEnabled ? 'ACTIVE' : 'INACTIVE'}
          isLoading={isProcessing}
          isEnabled={isEnabled}
          actions={[
            {
              action: isEnabled ? 'DISABLE' : 'ENABLE',
              label: isEnabled ? 'Disable' : 'Enable',
              isDanger: isEnabled,
              ...(!isEnabled
                ? {}
                : {
                    confirmProps: {
                      title:
                        'Are you sure you want to disable this connection?',
                      description:
                        'This will temporarily deactivate the QuickBooks integration. You can re-enable it at any time.',
                    },
                  }),
            },
          ]}
          type="QuickBooks"
          tags={[
            {
              label: 'Company ID',
              value: connection.companyId,
            },
          ]}
          onEdit={() => {
            setMode('EDIT');
            setShowWarning(true);
          }}
          onAction={handleAction}
        />
      )}
      {mode === 'EDIT' && (
        <>
          {description}
          <SettingCardEdit type="QuickBooks">
            <QuickBooksForm
              connection={connection}
              onClose={() => setMode('VIEW')}
            />
          </SettingCardEdit>
          {!isUndefined(connection?.refundLineItemId) && mode === 'EDIT' && (
            <MAlert type="warning" my={4}>
              <MText lineHeight="normal">
                If you alter Quickbooks account information, you may need to
                re-enter the Product ID below.
              </MText>
            </MAlert>
          )}
        </>
      )}
      {connection.companyId && <QuickBooksRefundSync connection={connection} />}
    </>
  );
};
